import { useEffect, useState, useCallback } from 'react';
import { useWindowsStore } from '../../store/windowsStore';
import DesktopIcon from './DesktopIcon';
import ManagedWindow from './ManagedWindow';
import Terminal from '../Terminal/Terminal';
import BootScreen from './BootScreen';
import {
  FiUser, FiFolder, FiMail, FiTrash2, FiAward, FiGithub, FiLinkedin, FiCodepen, FiPower, FiFileText, FiCpu, FiBriefcase,
} from 'react-icons/fi';
import './ForgeDesktop.css';

const APPS = [
  { id: 'projects',     label: 'Work',          icon: FiFolder,    tone: 'folder' },
  { id: 'about',        label: 'About',         icon: FiUser,      tone: 'app' },
  { id: 'skills',       label: 'Skills',        icon: FiCpu,       tone: 'cpu' },
  { id: 'experience',   label: 'Experience',    icon: FiBriefcase, tone: 'briefcase' },
  { id: 'resume',       label: 'Curriculo.pdf', icon: FiFileText,  tone: 'file' },
  { id: 'certificates', label: 'Awards',        icon: FiAward,     tone: 'award' },
  { id: 'contact',      label: 'Contact',       icon: FiMail,      tone: 'app' },
  { id: 'trash',        label: 'Trash',         icon: FiTrash2,    tone: 'trash' },
];

const SOCIALS = [
  { label: 'GitHub', href: 'https://github.com/IIBladeII', icon: FiGithub },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/pedro-riccio/', icon: FiLinkedin },
  { label: 'Code', href: 'https://github.com/IIBladeII?tab=repositories', icon: FiCodepen },
];

/**
 * Desktop — wallpaper translúcido + grade de ícones + dock + portal das janelas.
 * Fundo do quarto permanece o mesmo GIF pixel art customizado do Blade.
 */
export default function Desktop({ children, wallpaperMode = 'matrix', bootTimestamp, onOpenTerminal, onToggleWallpaper, onLeaveOS }) {
  const openWindow = useWindowsStore((s) => s.openWindow);
  const [now, setNow] = useState(() => new Date());
  const [isBooting, setIsBooting] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleBootComplete = useCallback(() => {
    setIsBooting(false);
  }, []);

  const openMappedWindow = (id) => openWindow(id);

  return (
    <main className="forge-stage" data-wallpaper={wallpaperMode}>
      {/* O FUNDO DA SALA PERMANECE O MESMO GIF */}
      <div className="forge-room" aria-hidden="true">
        <img
          src="/blade-room.gif"
          alt="Blade's Cyberpunk Room"
          className="forge-room-bg"
        />
        <div className="forge-room-dim" />
      </div>

      <section className="forge-monitor" aria-label="Pedro Riccio portfolio desktop">
        <div className="forge-monitor-bezel">
          <div className="forge-screen relative">
            <div className="forge-screen-noise" />

            {/* TELA DE BOOT DENTRO DO MONITOR */}
            {isBooting && (
              <BootScreen onComplete={handleBootComplete} />
            )}

            <div className="forge-icons" role="toolbar" aria-label="Portfolio applications">
              {APPS.map((app) => (
                <DesktopIcon key={app.id} id={app.id} label={app.label} icon={<app.icon />} tone={app.tone} onOpen={openMappedWindow} />
              ))}
            </div>
            <div className="forge-socials" aria-label="Social links">
              {SOCIALS.map(({ label, href, icon: Icon }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" className="forge-social-icon" aria-label={label} title={label}>
                  <Icon />
                  <span>{label}</span>
                </a>
              ))}
            </div>

            <div className="forge-windows-layer">
              {['about', 'projects', 'resume', 'contact', 'trash', 'certificates', 'terminal', 'skills', 'experience'].map((id) => (
                <ManagedWindow key={id} id={id}>
                  <div className="window-content forge-window-content">
                    {id === 'trash' ? <TrashContent />
                      : id === 'terminal' ? <Terminal />
                      : id === 'skills' || id === 'experience' ? children?.[id] ?? <PlaceholderContent appId={id} />
                      : children?.[id] ?? <PlaceholderContent appId={id} />}
                  </div>
                </ManagedWindow>
              ))}
            </div>

            <div className="forge-statusbar">
              <div className="forge-status-left">
                <span className="forge-led" />
                <span>PEDRO RICCIO /// SOFTWARE ENGINEER</span>
              </div>

              <div className="forge-status-right">
                <button type="button" onClick={onOpenTerminal} className="forge-status-action">
                  terminal
                </button>

                <div className="forge-clock">
                  <span>{now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                  <small>{now.toLocaleDateString('pt-BR')}</small>
                </div>

                {/* BOTÃO DESLIGAR PC (APENAS ÍCONE NO CANTO INFERIOR DIREITO) */}
                {onLeaveOS && (
                  <button
                    type="button"
                    onClick={onLeaveOS}
                    className="forge-power-icon-btn"
                    aria-label="Desligar PC"
                    title="Desligar PC"
                  >
                    <FiPower size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function PlaceholderContent({ appId }) {
  return (
    <div className="p-6 font-mono text-xs text-[var(--text-secondary)] space-y-2">
      <p className="text-[var(--neon-cyan)]">// {appId}.exe</p>
      <p>Conteúdo virá na Fase 4.</p>
      <p className="text-[var(--text-muted)]">placeholder window — drag/resize/close funcionam</p>
    </div>
  );
}

function TrashContent() {
  return (
    <div className="forge-empty-state">
      <FiTrash2 size={34} />
      <strong>Trash is empty</strong>
      <span>No discarded projects found.</span>
    </div>
  );
}
